import { z } from "zod";

export const baseTemplateSeoThemeSchema = z.object({
  colorScheme: z.enum(["light", "dark"]).optional(),
  themeColor: z.string(),
});

export const baseTemplateGeneralSchema = z.object({
  groom_name: z.string(),
  groom_nickname: z.string(),
  bride_name: z.string(),
  bride_nickname: z.string(),
  date: z.string(),
  time: z.string(),
  subdomain: z.string(),
  initials: z.object({
    groom: z.string().max(1),
    bride: z.string().max(1),
  }),
  parents: z.object({
    groom: z.object({
      father: z.string(),
      mother: z.string(),
      order: z.string(),
    }),
    bride: z.object({
      father: z.string(),
      mother: z.string(),
      order: z.string(),
    }),
  }),
});

export const baseTemplateHeroSchema = z.object({
  key: z.string(),
  enabled: z.boolean(),
  quotes: z.object({
    text: z.string(),
    source: z.string(),
  }),
});

export const baseTemplateEventsSchema = z.object({
  key: z.string(),
  enabled: z.boolean(),
  // ....
});

export const baseTemplateLocationItemSchema = z.object({
  activity: z.string(),
  venue: z.string(),
  address: z.string(),
  maps_link: z.url(),
  start_time: z.string(),
  end_time: z.string(),
});

export const baseTemplateLocationSchema = z.object({
  key: z.string(),
  enabled: z.boolean(),
  items: z.array(baseTemplateLocationItemSchema),
});

export const baseTemplateGallerySchema = z.object({
  key: z.string(),
  enabled: z.boolean(),
  imageCount: z.number().min(0).max(20),
  // ....
});

export const baseTemplateRsvpSchema = z.object({
  key: z.string(),
  enabled: z.boolean(),
  // ....
});

export const baseTemplateDressCodeSchema = z.object({
  key: z.string(),
  enabled: z.boolean(),
  style: z.string(),
  color: z.string(),
  hex_colors: z.array(z.string()).optional(),
});

export const baseTemplateGiftSchema = z.object({
  key: z.string(),
  enabled: z.boolean(),
  bank_accounts: z.array(z.object({
    bank_name: z.string(),
    account_name: z.string(),
    account_number: z.string(),
    icon: z.string(),
  })),
});

export const baseTemplateWishesSchema = z.object({
  key: z.string(),
  enabled: z.boolean(),
  // ....
});

export const baseTemplateOthersSchema = z.object({
  seo_theme: baseTemplateSeoThemeSchema.optional(),
  music: z.object({
    enabled: z.boolean(),
    src: z.url(),
  }),
  // ....
});

/**
 * Source of truth untuk contract `app.config.template` lintas template.
 *
 * Semua field shared baru harus ditambahkan di schema ini lebih dulu,
 * lalu diturunkan ke config tiap template melalui `defineTemplateConfig()`.
 * Dengan cara ini, repo memiliki satu contract runtime dan type-level
 * yang konsisten untuk seluruh template.
 */
export const baseTemplateConfigSchema = z.object({
  /**
   * Tambahkan field konfigurasi shared di sini saat ada kebutuhan baru.
   * Bila sebuah field hanya dipakai oleh satu template dan benar-benar
   * bersifat khusus, pertimbangkan untuk menambahkannya via wrapper lokal.
   */
  general: baseTemplateGeneralSchema,
  hero: baseTemplateHeroSchema,
  events: baseTemplateEventsSchema,
  location: baseTemplateLocationSchema,
  gallery: baseTemplateGallerySchema,
  rsvp: baseTemplateRsvpSchema,
  dress_code: baseTemplateDressCodeSchema,
  gift: baseTemplateGiftSchema,
  wishes: baseTemplateWishesSchema,
  others: baseTemplateOthersSchema,
});

/**
 * Tipe output hasil infer dari `baseTemplateConfigSchema`.
 *
 * Gunakan tipe ini untuk state aktif template, `app.config.ts`,
 * dan helper typed config lintas layer.
 */
export type BaseTemplateConfig = z.output<typeof baseTemplateConfigSchema>;
